import * as ReduxToolkit from "@reduxjs/toolkit";
import * as Redux from "redux";
import { today } from "./utils/time";

import { driverMenuSlice } from "./features/DriversListMenu/store";
import { loginSlice } from "./features/Login/store";
import { manualSlice } from "./features/Manual/store";
import { releaseNoteSlice } from "./features/RelaseNote/store";
import {
    breaksSlice,
    shiftMonitoringSlice,
    tripsSlice,
} from "./features/ShiftMonitoring/store";
import { capacitySlice } from "./features/UsageBased_Capacity/store";
import { paymentsSlice } from "./features/UsageBased_Summary/store";
import { usageTripSlice } from "./features/UsageBased_Trips/store";
import { detailSceenSlice } from "./features/WeeklyReport/DetailScreen/store";
import { weeklyReportsSlice } from "./features/WeeklyReport/store";

import { DriversRow } from "./api";
import { DriversList } from "./api/models/DriversList";
import {
    reserveShiftsSlice,
    reserveTripMapSlice,
    reserveTripsSlice,
} from "./features/Reserve/store";
import { SelectedTrip, VersionData } from "./interfaces";

export interface commonState {
    redirect?: string;
    // TODO: navigation へ
    selectedDriver: DriversRow | null;
    selectedOfficeId: number; // 初期表示に使っている
    officeIds: number[];
    officeNames: string[];
    driverNames: DriversList | null;
    navigation: SiteNavigationState;
    loading: boolean;
    errorMessage: string[] | null;
    user: string;
    versions: VersionData;
    selectedTrip: SelectedTrip | null;
    snackbarToggled: boolean;
    snackbarMsg: string;
    canUseMapNeighbors: boolean;
}
export interface SiteNavigationState {
    date: string;
    leftNavMenuIsCollapsed: boolean;
    driverName?: string;
    driverCode?: number;
    driverID?: number;
    workMinutes?: number;
}

export const commonInitState = (): commonState => {
    return {
        selectedDriver: null,
        selectedOfficeId: -1,
        officeIds: [],
        officeNames: [],
        driverNames: null,
        navigation: {
            date: today(),
            leftNavMenuIsCollapsed: true,
        },
        loading: false,
        errorMessage: null,
        redirect: "",
        user: "",
        versions: {
            build: "",
            version: "",
            dummy_enabled: false,
        },
        selectedTrip: null,
        snackbarToggled: false,
        snackbarMsg: "",
        canUseMapNeighbors: true,
    };
};

export const commonSlice = ReduxToolkit.createSlice({
    name: "common",
    initialState: commonInitState() as commonState,
    reducers: {
        resetState: (state) => {
            state = commonInitState();
            return state;
        },
        setSelectedTrip: (
            state,
            action: ReduxToolkit.PayloadAction<SelectedTrip | null>
        ) => {
            state.selectedTrip = action.payload;
            return state;
        },
        setCanUseMapNeighbors: (
            state,
            action: ReduxToolkit.PayloadAction<boolean>
        ) => {
            state.canUseMapNeighbors = action.payload;
            return state;
        },
        setSnackbarToggled: (
            state,
            action: ReduxToolkit.PayloadAction<boolean>
        ) => {
            state.snackbarToggled = action.payload;
            return state;
        },
        setSnackbarMsg: (state, action: ReduxToolkit.PayloadAction<string>) => {
            state.snackbarMsg = action.payload;
            return state;
        },
        setVersions: (
            state,
            action: ReduxToolkit.PayloadAction<VersionData>
        ) => {
            state.versions = action.payload;
            return state;
        },
        setRedirect: (state, action: ReduxToolkit.PayloadAction<string>) => {
            state.redirect = action.payload;
            return state;
        },
        toggleLeftMenuCollapsedState: (state) => {
            state.navigation.leftNavMenuIsCollapsed = !state.navigation
                .leftNavMenuIsCollapsed;
            return state;
        },
        redirectDone: (state) => {
            state.redirect = undefined;
            return state;
        },
        updateNavigation: (
            state,
            action: ReduxToolkit.PayloadAction<SiteNavigationState>
        ) => {
            state.navigation = action.payload;
            return state;
        },
        updateSelectedDriver: (
            state,
            action: ReduxToolkit.PayloadAction<DriversRow | null>
        ) => {
            state.selectedDriver = action.payload;
            return state;
        },
        updateSelectedDriverById: (
            state,
            action: ReduxToolkit.PayloadAction<number | null>
        ) => {
            if (
                !action.payload ||
                !state.driverNames ||
                !state.driverNames.drivers
            ) {
                state.selectedDriver = null;
                return state;
            }
            for (const d of state.driverNames.drivers) {
                if (d.driverId == action.payload) {
                    state.selectedDriver = d;
                    return state;
                }
            }
            return state;
        },
        setup: (
            state,
            action: ReduxToolkit.PayloadAction<
                [number, number[], string[], DriversList, VersionData]
            >
        ) => {
            state.selectedOfficeId = action.payload[0];
            state.officeIds = action.payload[1];
            state.officeNames = action.payload[2];
            state.driverNames = action.payload[3];
            state.versions = action.payload[4];
            return state;
        },
        updateSelectedOffice: (
            state,
            action: ReduxToolkit.PayloadAction<number>
        ) => {
            state.selectedOfficeId = action.payload;
            return state;
        },
        clearMessage: (state) => {
            state.errorMessage = null;
            return state;
        },
        setErrorMessage: (
            state,
            action: ReduxToolkit.PayloadAction<string[] | null>
        ) => {
            state.errorMessage = action.payload;
            return state;
        },
        appendErrorMessage: (
            state,
            action: ReduxToolkit.PayloadAction<string>
        ) => {
            if (!state.errorMessage) {
                state.errorMessage = [];
            }
            state.errorMessage.push(action.payload);
            return state;
        },
        showServerErrorMessage: (state) => {
            if (!state.errorMessage) {
                state.errorMessage = [];
            }
            state.errorMessage.push("サーバエラーが発生しました");
            return state;
        },
        startLoading: (state) => {
            state.loading = true;
            state.errorMessage = null;
            return state;
        },
        finishLoading: (state) => {
            state.loading = false;
            return state;
        },
    },
});

const rootReducer = Redux.combineReducers({
    breaks: breaksSlice.reducer,
    capacity: capacitySlice.reducer,
    common: commonSlice.reducer,
    detailScreen: detailSceenSlice.reducer,
    drivermenu: driverMenuSlice.reducer,
    login: loginSlice.reducer,
    manual: manualSlice.reducer,
    payments: paymentsSlice.reducer,
    releaseNote: releaseNoteSlice.reducer,
    shifts: shiftMonitoringSlice.reducer,
    trips: tripsSlice.reducer,
    reserveShifts: reserveShiftsSlice.reducer,
    reserveTrips: reserveTripsSlice.reducer,
    reserveTripMap: reserveTripMapSlice.reducer,
    usage: usageTripSlice.reducer,
    weeklyReports: weeklyReportsSlice.reducer,
});

function initStore() {
    return ReduxToolkit.configureStore({
        reducer: rootReducer,
    });
}

// rootReducerで設定した全てのSliceをクリアすること。消し忘れが厳禁
// ASK duck typing的な実装のやり方
export const clearStore = (): void => {
    console.debug("Resetting all states...");
    // ASK 通知不要なので store.dispatch(...) で包む必要はないのでは？
    // そもそもdispatchをかましても一瞬前のデータが出る
    store.dispatch(breaksSlice.actions.resetState());
    store.dispatch(capacitySlice.actions.resetState());
    store.dispatch(commonSlice.actions.resetState());
    store.dispatch(detailSceenSlice.actions.resetState());
    store.dispatch(driverMenuSlice.actions.resetState());
    store.dispatch(loginSlice.actions.resetState());
    store.dispatch(manualSlice.actions.resetState());
    store.dispatch(paymentsSlice.actions.resetState());
    store.dispatch(releaseNoteSlice.actions.resetState());
    store.dispatch(shiftMonitoringSlice.actions.resetState());
    store.dispatch(tripsSlice.actions.resetState());
    store.dispatch(reserveShiftsSlice.actions.resetState());
    store.dispatch(reserveTripsSlice.actions.resetState());
    store.dispatch(reserveTripMapSlice.actions.resetState());
    store.dispatch(usageTripSlice.actions.resetState());
    store.dispatch(weeklyReportsSlice.actions.resetState());
    console.debug("Resetting done. New state after reset...", store.getState());
};

export const store = initStore();
export type RootState = ReturnType<typeof rootReducer>;
